import { createApi } from '@reduxjs/toolkit/query/react';

import { IUploadAvatarPayload } from '../../../types/interfaces/app-file';
import { ICommunity } from '../../../types/interfaces/community';
import { COMMUNITIES, HTTP_METHOD, REDUX_CACHE, USERS } from '../../constants';
import * as endpoints from '../../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
} from '../../generic';
import { usersApi } from '../users/usersApi';
import type {
  ICommunitiesApiCommunityProfile,
  ICommunitiesApiGetCommunitiesResponse,
  ICommunitiesApiGetCommunityByIdResponse,
  ICommunitiesApiGetMyCommunitiesResponse,
} from './communities-api.types';

export const communitiesApi = createApi({
  reducerPath: 'communitiesApi',
  tagTypes: [
    USERS.ME,
    USERS.ME_DETAILED,
    COMMUNITIES.COMMUNITIES,
    COMMUNITIES.SELECTED_COMMUNITY,
    COMMUNITIES.MY_COMMUNITIES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
    REDUX_CACHE.LAZY_LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getCommunitiesApi: builder.query<
      ICommunitiesApiGetCommunitiesResponse,
      { searchValue: string; pageSize: number; page: number }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue, pageSize, page }) {
        const url =
          endpoints.Community.ROOT +
          '?' +
          getUrlParamsString({
            searchValue: searchValue,
            pageSize: pageSize,
            page: page,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _, arg) => [
        { type: REDUX_CACHE.LAZY_LIST, id: arg.page + '_' + arg.searchValue },
      ],
    }),
    getMyCommunitiesApi: builder.query<
      ICommunitiesApiGetMyCommunitiesResponse,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Community.MY,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, COMMUNITIES.MY_COMMUNITIES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getCommunityByIdApi: builder.query<
      ICommunitiesApiGetCommunityByIdResponse,
      string
    >({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: `${endpoints.Community.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: COMMUNITIES.SELECTED_COMMUNITY }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutCommunityApi: builder.mutation<
      ICommunitiesApiCommunityProfile,
      {
        id?: string;
        body: Partial<Omit<ICommunity, 'avatar'>> & {
          avatar?: IUploadAvatarPayload;
        };
      }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Community.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags([USERS.USERS]));
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }
        const affectedId = params.id ?? result?.id;
        return [
          {
            type: COMMUNITIES.COMMUNITIES,
            id: REDUX_CACHE.LIST,
          },
          {
            type: COMMUNITIES.SELECTED_COMMUNITY,
          },
          ...(affectedId
            ? [
                {
                  type: COMMUNITIES.COMMUNITIES,
                  id: affectedId,
                },
              ]
            : []),
        ];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    subscribeToCommunityApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Community.SUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags([USERS.ME, USERS.ME_DETAILED]));
        dispatch(
          usersApi.util.invalidateTags([
            { type: USERS.USER_COMMUNITIES, id: REDUX_CACHE.LIST },
          ]),
        );
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: REDUX_CACHE.LAZY_LIST },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.MY_COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    unsubscribeFromCommunityApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Community.UNSUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags([USERS.ME, USERS.ME_DETAILED]));
        dispatch(
          usersApi.util.invalidateTags([
            { type: USERS.USER_COMMUNITIES, id: REDUX_CACHE.LIST },
          ]),
        );
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: REDUX_CACHE.LAZY_LIST },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.MY_COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    changeCommunitySubscriptionStatusApi: builder.mutation<
      void,
      { id: string; body: { status: string; userId: string } }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Community.STATUS.replace('${1}', id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.MY_COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteCommunityApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Community.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetCommunitiesApiQuery: useGetCommunities,
  useGetMyCommunitiesApiQuery: useGetMyCommunities,
  useLazyGetCommunitiesApiQuery: useLazyGetCommunities,
  useGetCommunityByIdApiQuery: useGetCommunity,
  useLazyGetCommunityByIdApiQuery: useGetCommunityByIdLazy,
  usePostPutCommunityApiMutation: usePostPutCommunity,
  useSubscribeToCommunityApiMutation: useSubscribeToCommunity,
  useUnsubscribeFromCommunityApiMutation: useUnsubscribeFromCommunity,
  useDeleteCommunityApiMutation: useDeleteCommunity,
  useChangeCommunitySubscriptionStatusApiMutation:
    useChangeCommunitySubscriptionStatus,
} = communitiesApi;
